import { useEffect, useState } from 'react'
import { Eye, FileText } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import FuncionarioComplementoModal from '../components/FuncionarioComplementoModal'
import FuncionarioEnderecoModal from '../components/FuncionarioEnderecoModal'
import FuncionarioAdmissaoModal from '../components/FuncionarioAdmissaoModal'
import FuncionarioSalarioModal from '../components/FuncionarioSalarioModal'
import FuncionarioFeriasModal from '../components/FuncionarioFeriasModal'
import FuncionarioDependentesModal from '../components/FuncionarioDependentesModal'
import FuncionarioSeguroModal from '../components/FuncionarioSeguroModal'
import FuncionarioPlanoSaudeModal from '../components/FuncionarioPlanoSaudeModal'
import FuncionarioFiliacaoModal from '../components/FuncionarioFiliacaoModal'
import FuncionarioObservacoesModal from '../components/FuncionarioObservacoesModal'
import FuncionarioCertificadosModal from '../components/FuncionarioCertificadosModal'
import FuncionarioAlterarCodigoModal from '../components/FuncionarioAlterarCodigoModal'
import FuncionarioExamesModal from '../components/FuncionarioExamesModal'
import FuncionarioAfastamentosModal from '../components/FuncionarioAfastamentosModal'
import FuncionarioCargosModal from '../components/FuncionarioCargosModal'
import LancamentoOcorrencias from './LancamentoOcorrencias'
import ZonaSomenteLeitura from '../components/ZonaSomenteLeitura'

// Cadastro de Funcionários (TMOTCAD do ERP), gravando em frota.cadmot.
// Busca no padrão do cadastro de Clientes; edição no layout da tela do ERP (botões laterais ainda só visuais).

function IconPencil() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  )
}

type Form = Record<string, string>

const BOTOES_LATERAIS = [
  'Complemento', 'Endereço', 'Admissão / Demissão', 'Status', 'Histórico Cargos', 'Férias', 'Salário',
  'Dependentes', 'Seguro de Vida', 'Exames Médicos', 'Plano de saúde', 'Filiação',
  'Observações', 'Certificados/Registros', 'Ocorrências',
]

const FORM_INICIAL: Form = {
  motcod: '', motpra: '', motcen: '', motguerra: '', motpessoa: 'F', motnume: '', motcpfcgc: '',
  motstatus: '', motnome: '', motsexo: '', motiden: '', motideoem: '', motideeem: '',
  motidedem: '', mot_nro: '', mot_ser: '', motctpsest: '', motdnas: '', motnascid: '', mottitele: '', motpis: '',
  motcvf: 'N', estado_civil: '', motati: '', motafa: '', motusuinc: '', motdatin: '', motusualt: '', motdatal: '',
}

interface ItemLista {
  motcod: string; motnome: string; motguerra: string; motcpfcgc: string; empresa: string | null; cargo: string | null; motati: string
}

const SITUACAO: Record<string, string> = { A: 'Ativo', I: 'Inativo' }
const STATUS: Record<string, string> = { T: 'Trabalhando' }
// frota.motfil.motestciv (editado pela Filiação no ERP)
const ESTADO_CIVIL: Record<string, string> = { S: 'Solteiro', C: 'Casado', D: 'Divorciado', V: 'Viúvo', O: 'Outro' }

const fmtData = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
// Campo numérico zerado no banco aparece em branco na tela
const semZero = (v: any) => (v === null || v === undefined || String(v) === '0' ? '' : String(v))

// consulta: mesma tela só para visualizar (campos e janelas laterais travados)
export default function CadastroFuncionarios({ consulta = false }: { consulta?: boolean }) {
  const [busca, setBusca] = useState('')
  // consulta já abre com a lista (ativos por padrão)
  const [situacao, setSituacao] = useState<'ativos' | 'inativos' | 'todos'>('ativos')
  const [lista, setLista] = useState<ItemLista[]>([])
  const [carregando, setCarregando] = useState(false)

  const [modo, setModo] = useState<false | 'novo' | 'editar'>(false)
  const [form, setForm] = useState<Form>(FORM_INICIAL)
  const [cidadeNatal, setCidadeNatal] = useState({ des: '', uf: '' })
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [fotoUrl, setFotoUrl] = useState<string | null>(null)
  const [enviandoFoto, setEnviandoFoto] = useState(false)
  const [janela, setJanela] = useState<'complemento' | 'endereco' | 'admissao' | 'salario' | 'ferias' | 'dependentes' | 'seguro' | 'exames' | 'plano' | 'filiacao' | 'observacoes' | 'certificados' | 'ocorrencias' | 'alterarCodigo' | 'afastamentos' | 'cargos' | null>(null)

  const [empresas, setEmpresas] = useState<{ empcod: number; empfan: string }[]>([])
  const [cargos, setCargos] = useState<{ catcod: string; catdes: string }[]>([])
  const [estados, setEstados] = useState<{ est_cod: string; est_des: string }[]>([])

  const { empresaCod } = useEmpresa()
  const [nCadastroObrig, setNCadastroObrig] = useState(false)

  const set = (campo: string, valor: string) => setForm(p => ({ ...p, [campo]: valor }))

  useEffect(() => {
    if (!empresaCod) return
    api.get(`/funcionarios/parametros/${empresaCod}`).then(r => setNCadastroObrig(!!r.data.ncadastro_obrigatorio)).catch(() => {})
  }, [empresaCod])

  useEffect(() => {
    api.get('/erp/empresas').then(r => setEmpresas(r.data)).catch(() => {})
    api.get('/funcionarios/cargos').then(r => setCargos(r.data)).catch(() => {})
    api.get('/funcionarios/estados').then(r => setEstados(r.data)).catch(() => {})
  }, [])

  // Busca com debounce: código (qualquer tamanho) ou nome a partir de 2 letras
  useEffect(() => {
    const termo = busca.trim()
    if (consulta && (!termo || /^\d+$/.test(termo) || termo.length >= 2)) {
      const t = setTimeout(() => pesquisar(termo), termo ? 350 : 0)
      return () => clearTimeout(t)
    }
    if (!termo || (!/^\d+$/.test(termo) && termo.length < 2)) { setLista([]); return }
    const t = setTimeout(() => pesquisar(termo), 350)
    return () => clearTimeout(t)
  }, [busca, situacao]) // eslint-disable-line react-hooks/exhaustive-deps

  async function pesquisar(termo = busca.trim()) {
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios', { params: { status: consulta ? situacao : 'todos', busca: termo } })
      setLista(r.data)
    } finally {
      setCarregando(false)
    }
  }

  function trocarFotoUrl(url: string | null) {
    setFotoUrl(antiga => { if (antiga) URL.revokeObjectURL(antiga); return url })
  }

  async function carregarFoto(motcod: string) {
    try {
      const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/foto`, { responseType: 'blob' })
      trocarFotoUrl(URL.createObjectURL(r.data))
    } catch {
      trocarFotoUrl(null)
    }
  }

  // Reduz a imagem no navegador (máx. 600px, JPEG) antes de enviar
  function reduzirImagem(arquivo: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const escala = Math.min(1, 600 / Math.max(img.width, img.height))
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * escala)
        canvas.height = Math.round(img.height * escala)
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height)
        URL.revokeObjectURL(img.src)
        resolve(canvas.toDataURL('image/jpeg', 0.85))
      }
      img.onerror = () => reject(new Error('Arquivo de imagem inválido.'))
      img.src = URL.createObjectURL(arquivo)
    })
  }

  async function enviarFoto(arquivo: File | undefined) {
    if (!arquivo) return
    setErro(''); setAviso('')
    setEnviandoFoto(true)
    try {
      const imagem = await reduzirImagem(arquivo)
      await api.put(`/funcionarios/${encodeURIComponent(form.motcod)}/foto`, { imagem })
      await carregarFoto(form.motcod)
      setAviso('Foto atualizada.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || e?.message || 'Erro ao enviar a foto.')
    } finally {
      setEnviandoFoto(false)
    }
  }

  async function removerFoto() {
    if (!confirm('Remover a foto deste funcionário?')) return
    setErro(''); setAviso('')
    try {
      await api.delete(`/funcionarios/${encodeURIComponent(form.motcod)}/foto`)
      trocarFotoUrl(null)
      setAviso('Foto removida.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao remover a foto.')
    }
  }

  function abrirNovo() {
    setForm({ ...FORM_INICIAL, motati: 'A', motafa: 'T' })
    setCidadeNatal({ des: '', uf: '' })
    trocarFotoUrl(null)
    setErro(''); setAviso(''); setModo('novo')
  }

  async function abrirEditar(motcod: string) {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}`)
    const d = r.data
    setForm({
      ...Object.fromEntries(Object.keys(FORM_INICIAL).map(k => [k, d[k] === null || d[k] === undefined ? '' : String(d[k])])),
      motpra: semZero(d.motpra), motnume: semZero(d.motnume), motcpfcgc: semZero(d.motcpfcgc),
      mot_nro: semZero(d.mot_nro), mot_ser: semZero(d.mot_ser), motnascid: semZero(d.motnascid), mottitele: semZero(d.mottitele),
    })
    setCidadeNatal({ des: d.cidade_natal ?? '', uf: d.cidade_natal_uf ?? '' })
    carregarFoto(motcod)
    setErro(''); setModo('editar')
  }

  async function salvar() {
    setErro(''); setAviso('')
    if (!form.motcod.trim()) return setErro('Informe o código do funcionário.')
    if (!form.motnome.trim()) return setErro('Informe o nome completo.')
    if (!form.motpra) return setErro('Informe o estabelecimento.')
    if (nCadastroObrig && !form.motnume.trim()) return setErro('Número inválido. Informe o N. Cadastro.')
    setSalvando(true)
    try {
      const corpo = { ...form, empresa_logada: empresaCod }
      const r = modo === 'editar'
        ? await api.put(`/funcionarios/${encodeURIComponent(form.motcod)}`, corpo)
        : await api.post('/funcionarios', corpo)
      await abrirEditar(form.motcod.trim().toUpperCase())
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Funcionário salvo.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar funcionário.')
    } finally {
      setSalvando(false)
    }
  }

  // Dossiê do funcionário (PDF com tudo do cadastro)
  const [gerandoDossie, setGerandoDossie] = useState(false)
  async function abrirDossie() {
    setErro(''); setGerandoDossie(true)
    try {
      const r = await api.get(`/funcionarios/${encodeURIComponent(form.motcod)}/dossie`, { responseType: 'blob' })
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank')
    } catch {
      setErro('Erro ao gerar o dossiê do funcionário.')
    } finally {
      setGerandoDossie(false)
    }
  }

  function voltar() {
    setModo(false)
    if (busca.trim()) pesquisar()
  }

  // Funções de render (não componentes) pra não remontar o input a cada tecla
  const Campo = ({ campo, label, max, width, type = 'text', destaque, disabled }: {
    campo: string; label: string; max?: number; width: number; type?: string; destaque?: boolean; disabled?: boolean
  }) => (
    <div className={`field-cad${destaque ? ' destaque' : ''}`} style={{ flex: `0 0 ${width}px` }}>
      <label style={{ whiteSpace: 'nowrap' }}>{label}</label>
      <input type={type} value={form[campo]} maxLength={max} disabled={disabled}
        onChange={e => set(campo, type === 'date' ? e.target.value : e.target.value.toUpperCase())}
        style={{ width: '100%', boxSizing: 'border-box' }} />
    </div>
  )

  const SelectUf = (campo: string, label: string) => (
    <div className="field-cad" style={{ flex: '0 0 190px' }}>
      <label>{label}</label>
      <select value={form[campo]} onChange={e => set(campo, e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
        <option value="">--</option>
        {estados.map(e => <option key={e.est_cod} value={e.est_cod}>{e.est_cod} - {e.est_des}</option>)}
      </select>
    </div>
  )

  const termo = busca.trim()

  if (!modo) {
    return (
      <div className="card">
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem' }}>{consulta ? 'Consulta de Funcionários' : 'Funcionários'}</h2>

        <div className="busca-cliente-wrap" style={{ marginBottom: '1rem' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Código ou nome do funcionário</label>
            <div className="busca-cliente-row">
              <input type="text" placeholder="Digite para buscar..." value={busca} onChange={e => setBusca(e.target.value)} autoFocus />
              {consulta && (
                <select value={situacao} onChange={e => setSituacao(e.target.value as typeof situacao)} style={{ width: 130 }} title="Situação">
                  <option value="ativos">Ativos</option>
                  <option value="inativos">Inativos</option>
                  <option value="todos">Todos</option>
                </select>
              )}
              {!consulta && <button className="btn btn-primary btn-sm" onClick={abrirNovo} style={{ whiteSpace: 'nowrap' }}>+ Novo Funcionário</button>}
            </div>
          </div>
        </div>

        {(termo || consulta) && (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr><th>Código</th><th>Nome</th><th>Apelido</th><th>Cargo</th><th>Situação</th><th></th></tr>
              </thead>
              <tbody>
                {carregando && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>Carregando...</td></tr>}
                {!carregando && lista.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                    {!/^\d+$/.test(termo) && termo.length < 2 ? 'Digite ao menos 2 letras do nome.' : 'Nenhum funcionário encontrado.'}
                  </td></tr>
                )}
                {!carregando && consulta && lista.length === 500 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.78rem' }}>Mostrando os 500 primeiros — digite para filtrar.</td></tr>
                )}
                {!carregando && lista.map(c => (
                  <tr key={c.motcod}>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{c.motcod}</td>
                    <td style={{ fontWeight: 600 }}>{c.motnome || '—'}</td>
                    <td style={{ fontSize: '0.82rem', color: '#6b7280' }}>{c.motguerra || '—'}</td>
                    <td style={{ fontSize: '0.82rem' }}>{c.cargo || '—'}</td>
                    <td>
                      <span className={`badge ${c.motati === 'A' ? 'badge-success' : 'badge-default'}`}>{SITUACAO[c.motati] ?? (c.motati || '—')}</span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div className="row-actions">
                        <button className="row-action-btn row-action-btn--edit" onClick={() => abrirEditar(c.motcod)} title={consulta ? 'Visualizar' : 'Editar'}>{consulta ? <Eye size={15} /> : <IconPencil />}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  return (
    <ZonaSomenteLeitura ativo={consulta}>
    <div className="cad-veiculos" style={{ display: 'flex', gap: '0.65rem', alignItems: 'flex-start' }}>
      <div className="card" style={{ flexShrink: 0, maxWidth: 820 }}>
        <h2 style={{ marginTop: 0, marginBottom: '0.9rem' }}>
          {consulta ? 'Consulta de Funcionário' : modo === 'novo' ? 'Novo Funcionário' : 'Cadastro de Funcionários'}
        </h2>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', flex: 1 }}>
            <div className="cad-func-linha">
              {Campo({ campo: 'motcod', label: 'Código', max: 10, width: 110, destaque: true, disabled: modo === 'editar' })}
              {modo === 'editar' && !consulta && (
                <button className="btn btn-outline btn-sm" style={{ alignSelf: 'flex-end', marginBottom: 2, whiteSpace: 'nowrap' }}
                  title="Trocar o código deste funcionário em todo o sistema (só administrador)" onClick={() => setJanela('alterarCodigo')}>
                  Alterar código
                </button>
              )}
              <div className="field-cad" style={{ flex: '0 0 230px' }}>
                <label>Estabelecimento</label>
                <select value={form.motpra} onChange={e => set('motpra', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                  <option value="">--</option>
                  {empresas.map(e => <option key={e.empcod} value={String(e.empcod)}>{e.empcod} - {e.empfan}</option>)}
                </select>
              </div>
              {Campo({ campo: 'motcen', label: 'Local (p/ Agrupamento)', max: 2, width: 130 })}
            </div>
            <div className="cad-func-linha">
              {Campo({ campo: 'motguerra', label: 'Apelido', max: 20, width: 180 })}
              <div className="field-cad" style={{ flex: '0 0 100px' }}>
                <label>Pessoa</label>
                <select value={form.motpessoa} style={{ width: '100%', boxSizing: 'border-box' }}
                  onChange={e => setForm(p => ({ ...p, motpessoa: e.target.value, ...(e.target.value === 'J' ? { motiden: '' } : {}) }))}>
                  <option value="F">Física</option>
                  <option value="J">Jurídica</option>
                </select>
              </div>
              {Campo({ campo: 'motnume', label: nCadastroObrig ? 'N. Cadastro *' : 'N. Cadastro', max: 9, width: 100 })}
              {Campo({ campo: 'motcpfcgc', label: form.motpessoa === 'J' ? 'CNPJ' : 'CPF', max: 14, width: 140 })}
            </div>
            <div className="cad-func-linha">
              <div className="field-cad" style={{ flex: '0 0 260px' }}>
                <label>Cargo</label>
                <select value={form.motstatus} onChange={e => set('motstatus', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                  <option value="">--</option>
                  {cargos.map(c => <option key={c.catcod} value={c.catcod}>{c.catdes}</option>)}
                </select>
              </div>
            </div>
            <div className="cad-func-linha">
              {Campo({ campo: 'motnome', label: 'Nome Completo', max: 30, width: 260, destaque: true })}
              <div className="field-cad" style={{ flex: '0 0 120px' }}>
                <label>Sexo</label>
                <select value={form.motsexo} onChange={e => set('motsexo', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                  <option value="">--</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
              </div>
              {Campo({ campo: 'motdnas', label: 'D. Nascimento', type: 'date', width: 140 })}
            </div>
          </div>

          {/* Foto enviada pelo web (frota.cadmot_foto); só depois do funcionário salvo */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', width: 120, flexShrink: 0 }}>
            <div style={{
              width: 120, height: 145, border: '1px solid #d1d5db', borderRadius: 8, background: '#f9fafb', overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '0.7rem', textAlign: 'center',
            }}>
              {fotoUrl
                ? <img src={fotoUrl} alt="Foto do funcionário" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : enviandoFoto ? 'Enviando...' : 'Sem foto'}
            </div>
            {!consulta && <label className="btn btn-secondary btn-sm"
              style={{ justifyContent: 'center', cursor: modo === 'novo' || enviandoFoto ? 'not-allowed' : 'pointer', opacity: modo === 'novo' ? 0.5 : 1 }}
              title={modo === 'novo' ? 'Salve o funcionário primeiro' : undefined}>
              {fotoUrl ? 'Trocar foto' : 'Adicionar foto'}
              <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={modo === 'novo' || enviandoFoto}
                onChange={e => { enviarFoto(e.target.files?.[0]); e.target.value = '' }} />
            </label>}
            {fotoUrl && !consulta && (
              <button className="btn btn-sm" style={{ justifyContent: 'center', color: '#dc2626' }} onClick={removerFoto}>Remover</button>
            )}
          </div>
        </div>

        <h3 style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '1rem 0 0.5rem' }}>
          Documentos
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
          <div className="cad-func-linha">
            {Campo({ campo: 'motiden', label: 'C. Identidade', max: 12, width: 120, disabled: form.motpessoa === 'J' })}
            {Campo({ campo: 'motideoem', label: 'Órgão Emissor', max: 15, width: 120 })}
            {SelectUf('motideeem', 'Estado Emissor')}
            {Campo({ campo: 'motidedem', label: 'Data', type: 'date', width: 140 })}
          </div>
          <div className="cad-func-linha">
            {Campo({ campo: 'mot_nro', label: 'CTPS Número', max: 15, width: 140 })}
            {Campo({ campo: 'mot_ser', label: 'Série', max: 6, width: 70 })}
            {SelectUf('motctpsest', 'Estado Emissor')}
          </div>
          <div className="cad-func-linha">
            <div className="field-cad" style={{ flex: '0 0 260px' }}>
              <label>Cidade Natal</label>
              <LookupSelect
                endpoint="/erp/cidades"
                codKey="cid_cod"
                descKey="cid_des"
                tagKey="cid_estcod"
                codValue={form.motnascid}
                descValue={cidadeNatal.des ? `${cidadeNatal.des}${cidadeNatal.uf ? ' - ' + cidadeNatal.uf : ''}` : ''}
                onSelect={(cod, desc, item) => { set('motnascid', cod); setCidadeNatal({ des: desc, uf: item?.cid_estcod ?? '' }) }}
                placeholder="Digite para buscar..."
                somenteComTexto
                listWidth={300}
              />
            </div>
            {Campo({ campo: 'mottitele', label: 'Título Eleitor', max: 12, width: 130 })}
            {Campo({ campo: 'motpis', label: 'PIS', max: 11, width: 120 })}
          </div>
          <div className="cad-func-linha">
            <div className="field-cad" style={{ flex: '0 0 120px' }}>
              <label>Estado Civil</label>
              <input value={ESTADO_CIVIL[form.estado_civil] ?? form.estado_civil} disabled title="Alterado pela Filiação" />
            </div>
            <div className="field-cad" style={{ flex: '0 0 110px' }}>
              <label>Ordem de Frete</label>
              <select value={form.motcvf} onChange={e => set('motcvf', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                <option value="N">Não</option>
                <option value="S">Sim</option>
              </select>
            </div>
            <div className="field-cad" style={{ flex: '0 0 120px' }}>
              <label>Situação</label>
              <input value={SITUACAO[form.motati] ?? form.motati} disabled title="Alterada pela Admissão / Demissão" />
            </div>
            <div className="field-cad" style={{ flex: '0 0 130px' }}>
              <label>Status</label>
              <input value={STATUS[form.motafa] ?? form.motafa} disabled title="Alterado pela aba Status" />
            </div>
          </div>
        </div>

        {erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0.8rem 0 0' }}>{erro}</p>}
        {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '0.8rem 0 0' }}>{aviso}</p>}

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem',
          marginTop: '0.9rem', paddingTop: '0.7rem', borderTop: '1px solid #edeff2', fontSize: '0.75rem', color: '#6b7280',
        }}>
          <span>
            {modo === 'editar' && <>
              Inclusão: <b style={{ color: '#dc2626' }}>{form.motusuinc || '—'}</b> {fmtData(form.motdatin)}
              {' · '}Alteração: <b style={{ color: '#1a6ea8' }}>{form.motusualt || '—'}</b> {fmtData(form.motdatal)}
            </>}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={voltar} data-livre>Voltar</button>
            {!consulta && <button className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>}
          </div>
        </div>
      </div>

      {/* Botões laterais — como no ERP, só habilitam depois de salvar o funcionário; os sem ação ainda são só visuais */}
      <div className="cad-veiculos-lateral" style={{ width: 170, flexShrink: 0 }} data-livre>
        <p className="cad-veiculos-lateral-titulo">Mais opções</p>
        <button className="btn btn-sm" disabled={modo === 'novo' || gerandoDossie} onClick={abrirDossie}
          title={modo === 'novo' ? 'Salve o funcionário primeiro' : 'Tudo do funcionário num PDF só'}
          style={{ background: '#1b3f7b', color: '#fff', borderColor: '#1b3f7b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <FileText size={14} /> {gerandoDossie ? 'Gerando...' : 'Dossiê (PDF)'}
        </button>
        {BOTOES_LATERAIS.map(b => {
          const acao = b === 'Complemento' ? () => setJanela('complemento')
            : b === 'Endereço' ? () => setJanela('endereco')
            : b === 'Admissão / Demissão' ? () => setJanela('admissao')
            : b === 'Status' ? () => setJanela('afastamentos')
            : b === 'Histórico Cargos' ? () => setJanela('cargos')
            : b === 'Salário' ? () => setJanela('salario')
            : b === 'Férias' ? () => setJanela('ferias')
            : b === 'Dependentes' ? () => setJanela('dependentes')
            : b === 'Seguro de Vida' ? () => setJanela('seguro')
            : b === 'Exames Médicos' ? () => setJanela('exames')
            : b === 'Plano de saúde' ? () => setJanela('plano')
            : b === 'Filiação' ? () => setJanela('filiacao')
            : b === 'Observações' ? () => setJanela('observacoes')
            : b === 'Certificados/Registros' ? () => setJanela('certificados')
            : b === 'Ocorrências' ? () => setJanela('ocorrencias')
            : null
          return (
            <button key={b} className="btn btn-sm" disabled={modo === 'novo'} onClick={acao ?? undefined}
              title={modo === 'novo' ? 'Salve o funcionário primeiro' : acao ? undefined : 'Em breve'}>{b}</button>
          )
        })}
      </div>

      {janela === 'complemento' && <FuncionarioComplementoModal motcod={form.motcod} onClose={() => setJanela(null)} />}
      {janela === 'endereco' && <FuncionarioEnderecoModal motcod={form.motcod} onClose={() => setJanela(null)} />}
      {janela === 'afastamentos' && <FuncionarioAfastamentosModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)} />}
      {janela === 'cargos' && (
        <FuncionarioCargosModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)}
          onCargoAtual={c => setForm(p => ({ ...p, motstatus: c }))} />
      )}
      {janela === 'salario' &&<FuncionarioSalarioModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)} />}
      {janela === 'ferias' && <FuncionarioFeriasModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)} />}
      {janela === 'dependentes' && <FuncionarioDependentesModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)} />}
      {janela === 'seguro' && <FuncionarioSeguroModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)} />}
      {janela === 'plano' && <FuncionarioPlanoSaudeModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)} />}
      {janela === 'alterarCodigo' && (
        <FuncionarioAlterarCodigoModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)}
          onAlterado={async novo => { setJanela(null); await abrirEditar(novo); setAviso(`Código alterado para ${novo}.`) }} />
      )}
      {janela === 'ocorrencias' && (
        <div className="modal-overlay" style={{ zIndex: 1200 }}>
          <div className="modal-box" style={{ width: '96vw', maxWidth: 1240, maxHeight: '94vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
              <h2>Ocorrências — {form.motcod} - {form.motnome}</h2>
              <button className="modal-fechar" onClick={() => setJanela(null)}>✕</button>
            </div>
            <div className="modal-corpo" style={{ overflowY: 'auto' }} data-livre>
              <LancamentoOcorrencias funcionario={{ cod: form.motcod, nome: form.motnome }} consulta={consulta} />
            </div>
          </div>
        </div>
      )}
      {janela === 'certificados' &&<FuncionarioCertificadosModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)} />}
      {janela === 'observacoes' &&<FuncionarioObservacoesModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)} />}
      {janela === 'filiacao' && (
        <FuncionarioFiliacaoModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)}
          onSalvo={ec => setForm(f => ({ ...f, estado_civil: ec }))} />
      )}
      {janela === 'exames' && <FuncionarioExamesModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)} />}
      {janela === 'admissao' && (
        <FuncionarioAdmissaoModal motcod={form.motcod} motnome={form.motnome} onClose={() => setJanela(null)}
          onSalvo={() => api.get(`/funcionarios/${encodeURIComponent(form.motcod)}`)
            .then(r => setForm(p => ({ ...p, motati: String(r.data.motati ?? ''), motafa: String(r.data.motafa ?? '') })))
            .catch(() => {})} />
      )}
    </div>
    </ZonaSomenteLeitura>
  )
}
